import React, { useState, useEffect, useRef } from 'react';

const TFUNCheckIn = ({ locationId }) => {
    // Add CSS animations
    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
            }
        `;
        document.head.appendChild(style);
        return () => document.head.removeChild(style);
    }, []);
    // Name search states
    const [searchQuery, setSearchQuery] = useState('');
    const [activeMembers, setActiveMembers] = useState([]);
    const [filteredMembers, setFilteredMembers] = useState([]);
    const [selectedMember, setSelectedMember] = useState(null);
    const [showDropdown, setShowDropdown] = useState(false);
    const [loadingMembers, setLoadingMembers] = useState(false);
    const [membersError, setMembersError] = useState('');
    
    // Existing states
    const [showSuccess, setShowSuccess] = useState(false);
    const [showError, setShowError] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [memberName, setMemberName] = useState('');
    const [memberPicture, setMemberPicture] = useState(null);
    const [checkinCount, setCheckinCount] = useState(0);
    const [locationName, setLocationName] = useState('Loading...');
    const [locationError, setLocationError] = useState(false);
    const [loading, setLoading] = useState(false);
    
    // Refs for managing dropdown
    const searchInputRef = useRef(null);
    const dropdownRef = useRef(null);

    // TFUN Color Scheme
    const colors = {
        primary: '#1e40af',
        primaryDark: '#1e3a8a',
        secondary: '#f8fafc',
        accent: '#3b82f6',
        text: '#ffffff',
        textLight: '#e2e8f0',
        success: '#10b981',
        warning: '#f59e0b',
        danger: '#ef4444',
        background: '#000000'
    };

    // Load location name
    useEffect(() => {
        const loadLocationName = async () => {
            try {
                console.log(`🏢 Loading location details for: ${locationId}`);
                
                // Use the location details API to get the actual location name
                const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ghl/location-details/${locationId}`);
                
                if (response.ok) {
                    const data = await response.json();
                    const businessName = data.business_name || data.name || data.companyName;
                    
                    if (businessName) {
                        console.log(`✅ Location loaded: ${businessName}`);
                        setLocationName(businessName);
                        setLocationError(false);
                    } else {
                        console.log('⚠️ No business name found in location data');
                        setLocationName('Unknown Location');
                        setLocationError(true);
                    }
                } else {
                    console.log(`❌ Failed to load location details: ${response.status}`);
                    setLocationError(true);
                    setLocationName('Location Not Found');
                }
            } catch (error) {
                console.error('❌ Error loading location name:', error);
                setLocationError(true);
                setLocationName('Location Not Found');
            }
        };
        
        if (locationId && locationId !== 'Unknown' && locationId.trim() !== '') {
            loadLocationName();
        } else {
            console.log('⚠️ No valid location ID provided');
            setLocationError(true);
            setLocationName('No Location ID Provided');
        }
    }, [locationId]);

    // Load active members for this location
    useEffect(() => {
        const loadActiveMembers = async () => {
            if (!locationId || locationError) return;
            
            setLoadingMembers(true);
            setMembersError('');
            
            try {
                console.log(`👥 Loading active members for location: ${locationId}`);
                
                const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ghl/contacts/${locationId}`);
                
                if (response.ok) {
                    const data = await response.json();
                    const members = data.contacts || [];
                    
                    console.log(`✅ Loaded ${members.length} active members`);
                    setActiveMembers(members);
                    setMembersError('');
                } else {
                    console.log(`❌ Failed to load active members: ${response.status}`);
                    setMembersError('Unable to load members. Please refresh the page.');
                    setActiveMembers([]);
                }
            } catch (error) {
                console.error('❌ Error loading active members:', error);
                setMembersError('Network error loading members. Please check your connection.');
                setActiveMembers([]);
            } finally {
                setLoadingMembers(false);
            }
        };
        
        loadActiveMembers();
    }, [locationId, locationError]);

    // Filter members based on search query
    useEffect(() => {
        if (searchQuery.length < 2) {
            setFilteredMembers([]);
            setShowDropdown(false);
            return;
        }

        const query = searchQuery.toLowerCase().trim();
        const matches = activeMembers.filter(member => {
            const memberName = member.contactName || `${member.firstName || ''} ${member.lastName || ''}`.trim();
            return memberName.toLowerCase().includes(query);
        }).slice(0, 10); // Limit to 10 results

        setFilteredMembers(matches);
        setShowDropdown(matches.length > 0);
    }, [searchQuery, activeMembers]);

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
                searchInputRef.current && !searchInputRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Handle member selection
    const handleMemberSelect = (member) => {
        const memberName = member.contactName || `${member.firstName || ''} ${member.lastName || ''}`.trim();
        setSelectedMember(member);
        setSearchQuery(memberName);
        setShowDropdown(false);
    };

    // Clear selection and start over
    const handleStartOver = () => {
        setSelectedMember(null);
        setSearchQuery('');
        setShowDropdown(false);
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }
    };

    // Update page title
    useEffect(() => {
        document.title = 'Check In - Studio Manager V5';
    }, []);

    // Utility function to capitalize names
    const capitalizeName = (name) => {
        if (!name) return 'Unknown';
        return name.split(' ').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        ).join(' ');
    };

    // Auto-reload after success
    useEffect(() => {
        if (showSuccess) {
            const timer = setTimeout(() => {
                setShowSuccess(false);
                setSuccessMessage('');
                setMemberName('');
                setCheckinCount(0);
                setSelectedMember(null);
                setSearchQuery('');
            }, 5000);
            
            return () => clearTimeout(timer);
        }
    }, [showSuccess]);

    // Auto-reload after error
    useEffect(() => {
        if (showError) {
            const timer = setTimeout(() => {
                setShowError(false);
                setErrorMessage('');
                handleStartOver();
            }, 4000);
            
            return () => clearTimeout(timer);
        }
    }, [showError]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedMember) return;
        
        setLoading(true);
        
        try {
            console.log(`🚀 Checking in member: ${selectedMember.contactName} (${selectedMember.id})`);
            
            // Check-in using contact ID instead of PIN
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ghl/checkin/${locationId}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    contact_id: selectedMember.id,
                    contact_name: selectedMember.contactName || `${selectedMember.firstName || ''} ${selectedMember.lastName || ''}`.trim()
                })
            });
            
            let result = null;
            try {
                result = await response.json();
            } catch (e) {
                console.error('Failed to parse response as JSON:', e);
                if (!response.ok) {
                    setErrorMessage('Check-in failed. Please try again.');
                    setShowError(true);
                    return;
                }
            }
            
            if (response.ok && result && result.success) {
                const memberDisplayName = selectedMember.contactName || `${selectedMember.firstName || ''} ${selectedMember.lastName || ''}`.trim();
                setMemberName(capitalizeName(memberDisplayName));
                setMemberPicture(result.member_picture || null);
                setCheckinCount(result.checkin_count || 0);
                setSuccessMessage(`Welcome ${capitalizeName(memberDisplayName)}! Check-in successful.`);
                setShowSuccess(true);
                setSelectedMember(null);
                setSearchQuery('');
            } else {
                // Handle specific error cases
                if (response.status === 404) {
                    setErrorMessage('Member not found. Please try selecting again.');
                } else if (result && result.detail) {
                    setErrorMessage(result.detail);
                } else if (result && result.message) {
                    setErrorMessage(result.message);
                } else {
                    setErrorMessage('Check-in failed. Please try again.');
                }
                setShowError(true);
            }
        } catch (error) {
            console.error('Check-in error:', error);
            setErrorMessage('Network error during check-in. Please try again.');
            setShowError(true);
        } finally {
            setLoading(false);
        }
    };

    if (showSuccess) {
        return (
            <div style={{
                minHeight: '100vh',
                backgroundColor: colors.background,
                padding: '40px 20px',
                textAlign: 'center',
                color: colors.text,
                fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, sans-serif',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center'
            }}>
                <div style={{
                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                    border: '2px solid #10b981',
                    borderRadius: '20px',
                    padding: '60px 40px',
                    maxWidth: '600px',
                    width: '100%',
                    backdropFilter: 'blur(10px)'
                }}>
                    <div style={{
                        fontSize: '80px',
                        marginBottom: '20px',
                        animation: 'pulse 2s infinite'
                    }}>
                        ✅
                    </div>
                    
                    {memberPicture && (
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            marginBottom: '20px'
                        }}>
                            <img 
                                src={memberPicture} 
                                alt={memberName}
                                style={{
                                    width: '120px',
                                    height: '120px',
                                    borderRadius: '50%',
                                    objectFit: 'cover',
                                    border: '4px solid #10b981'
                                }}
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                }}
                            />
                        </div>
                    )}
                    
                    <h1 style={{
                        fontSize: '2.5rem',
                        marginBottom: '10px',
                        fontWeight: '700',
                        color: colors.success
                    }}>
                        Check-In Successful!
                    </h1>
                    
                    <h2 style={{
                        fontSize: '2rem',
                        marginBottom: '20px',
                        fontWeight: '500',
                        color: colors.text
                    }}>
                        Welcome {memberName}!
                    </h2>
                    
                    {checkinCount > 0 && (
                        <div style={{
                            backgroundColor: 'rgba(59, 130, 246, 0.1)',
                            border: '1px solid #3b82f6',
                            borderRadius: '12px',
                            padding: '20px',
                            marginBottom: '30px'
                        }}>
                            <div style={{
                                fontSize: '1.5rem',
                                color: colors.accent,
                                fontWeight: '600'
                            }}>
                                Total Check-ins: {checkinCount}
                            </div>
                        </div>
                    )}
                    
                    <p style={{
                        fontSize: '1.2rem',
                        color: colors.textLight,
                        marginBottom: '20px'
                    }}>
                        Have an awesome training session!
                    </p>
                    
                    <div style={{
                        fontSize: '1rem',
                        color: colors.textLight,
                        opacity: 0.7
                    }}>
                        Returning to check-in screen in 5 seconds...
                    </div>
                </div>
                
                <style>
                    {`
                        @keyframes pulse {
                            0%, 100% { transform: scale(1); }
                            50% { transform: scale(1.1); }
                        }
                    `}
                </style>
            </div>
        );
    }

    if (showError) {
        return (
            <div style={{
                minHeight: '100vh',
                backgroundColor: colors.background,
                padding: '40px 20px',
                textAlign: 'center',
                color: colors.text,
                fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, sans-serif',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center'
            }}>
                <div style={{
                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                    border: '2px solid #ef4444',
                    borderRadius: '20px',
                    padding: '60px 40px',
                    maxWidth: '600px',
                    width: '100%',
                    backdropFilter: 'blur(10px)'
                }}>
                    <div style={{
                        fontSize: '80px',
                        marginBottom: '20px',
                        animation: 'shake 0.5s ease-in-out'
                    }}>
                        ❌
                    </div>
                    
                    <h1 style={{
                        fontSize: '2.5rem',
                        marginBottom: '20px',
                        fontWeight: '700',
                        color: colors.danger
                    }}>
                        {errorMessage}
                    </h1>
                    
                    <p style={{
                        fontSize: '1.2rem',
                        color: colors.textLight,
                        marginBottom: '20px'
                    }}>
                        Please check your PIN and try again
                    </p>
                    
                    <div style={{
                        fontSize: '1rem',
                        color: colors.textLight,
                        opacity: 0.7
                    }}>
                        Returning to check-in screen in 4 seconds...
                    </div>
                </div>
                
                <style>
                    {`
                        @keyframes shake {
                            0%, 100% { transform: translateX(0); }
                            25% { transform: translateX(-10px); }
                            75% { transform: translateX(10px); }
                        }
                        @keyframes pulse {
                            0%, 100% { transform: scale(1); }
                            50% { transform: scale(1.1); }
                        }
                    `}
                </style>
            </div>
        );
    }

    return (
        <div style={{
            minHeight: '100vh',
            backgroundColor: colors.background,
            padding: '40px 20px',
            textAlign: 'center',
            color: colors.text,
            fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, sans-serif',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative'
        }}>
            {/* Version Indicator */}
            <div style={{
                position: 'fixed',
                bottom: '16px',
                left: '16px',
                backgroundColor: 'rgba(30, 64, 175, 0.9)',
                color: 'white',
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '12px',
                fontFamily: 'monospace',
                zIndex: 1000
            }}>
                v2.4.6-error-display-fixed
            </div>
            {/* Header */}
            <h1 style={{ 
                fontSize: '3.5rem', 
                marginBottom: '10px',
                fontWeight: '700',
                textShadow: '2px 2px 4px rgba(255,255,255,0.1)',
                background: 'linear-gradient(135deg, #ffffff 0%, #e2e8f0 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
            }}>
                {locationError ? (
                    <div style={{
                        color: colors.danger,
                        textAlign: 'center'
                    }}>
                        Please check your link and try again, no location found.
                    </div>
                ) : (
                    locationName
                )}
            </h1>
            
            <h2 style={{ 
                fontSize: '2rem', 
                marginBottom: '50px',
                fontWeight: '300',
                opacity: '0.9',
                color: colors.textLight
            }}>
                Member Check-In
            </h2>

            {/* Check-in Form */}
            {!locationError ? (
                <div style={{ width: '100%', maxWidth: '500px', position: 'relative' }}>
                    {/* Loading Members State */}
                    {loadingMembers && (
                        <div style={{ 
                            textAlign: 'center', 
                            marginBottom: '20px',
                            color: colors.textLight,
                            fontSize: '1.1rem'
                        }}>
                            <div style={{ 
                                display: 'inline-block',
                                width: '20px',
                                height: '20px',
                                border: '2px solid #ffffff',
                                borderTopColor: 'transparent',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite',
                                marginRight: '10px'
                            }}></div>
                            Loading members...
                        </div>
                    )}

                    {/* Members Error State */}
                    {membersError && (
                        <div style={{ 
                            textAlign: 'center', 
                            marginBottom: '20px',
                            color: colors.danger,
                            fontSize: '1.1rem'
                        }}>
                            {membersError}
                        </div>
                    )}

                    {/* Member Selection or Confirmation */}
                    {!selectedMember ? (
                        /* Search Interface */
                        <div style={{ marginBottom: '40px', position: 'relative' }}>
                            <label style={{ 
                                display: 'block',
                                fontSize: '1.3rem', 
                                marginBottom: '20px',
                                fontWeight: '500',
                                color: colors.text,
                                textAlign: 'center'
                            }}>
                                Who's checking in today?
                            </label>
                            
                            <div style={{ position: 'relative' }}>
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Start typing your name..."
                                    autoComplete="off"
                                    autoFocus={true}
                                    disabled={loading || loadingMembers || membersError}
                                    style={{
                                        width: '100%',
                                        padding: '20px',
                                        fontSize: '1.5rem',
                                        textAlign: 'center',
                                        backgroundColor: '#333333',
                                        color: '#ffffff',
                                        border: '2px solid #555555',
                                        borderRadius: '15px',
                                        outline: 'none',
                                        fontWeight: '500',
                                        transition: 'all 0.3s ease',
                                        opacity: (loading || loadingMembers || membersError) ? 0.7 : 1
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = '#ffffff';
                                        e.target.style.boxShadow = '0 0 20px rgba(255,255,255,0.3)';
                                    }}
                                    onBlur={(e) => {
                                        // Don't remove focus styles immediately to allow dropdown clicks
                                        setTimeout(() => {
                                            e.target.style.borderColor = '#555555';
                                            e.target.style.boxShadow = 'none';
                                        }, 200);
                                    }}
                                />

                                {/* Search Results Dropdown */}
                                {showDropdown && (
                                    <div 
                                        ref={dropdownRef}
                                        style={{
                                            position: 'absolute',
                                            top: '100%',
                                            left: '0',
                                            right: '0',
                                            backgroundColor: '#2a2a2a',
                                            border: '2px solid #555555',
                                            borderTop: 'none',
                                            borderRadius: '0 0 15px 15px',
                                            maxHeight: '300px',
                                            overflowY: 'auto',
                                            zIndex: 1000,
                                            boxShadow: '0 10px 25px rgba(0,0,0,0.5)'
                                        }}
                                    >
                                        {filteredMembers.map((member, index) => {
                                            const displayName = member.contactName || `${member.firstName || ''} ${member.lastName || ''}`.trim();
                                            return (
                                                <div
                                                    key={member.id}
                                                    onClick={() => handleMemberSelect(member)}
                                                    style={{
                                                        padding: '15px 20px',
                                                        borderBottom: index < filteredMembers.length - 1 ? '1px solid #555555' : 'none',
                                                        cursor: 'pointer',
                                                        fontSize: '1.2rem',
                                                        color: colors.text,
                                                        backgroundColor: 'transparent',
                                                        transition: 'background-color 0.2s ease'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.target.style.backgroundColor = '#3a3a3a';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.target.style.backgroundColor = 'transparent';
                                                    }}
                                                >
                                                    {displayName}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {/* Search Helper Text */}
                            <div style={{
                                textAlign: 'center',
                                marginTop: '15px',
                                fontSize: '1rem',
                                color: colors.textLight,
                                opacity: '0.7'
                            }}>
                                {searchQuery.length === 0 && "Type at least 2 characters to search"}
                                {searchQuery.length === 1 && "Keep typing..."}
                                {searchQuery.length >= 2 && filteredMembers.length === 0 && "Hmmm. I can't find that name."}
                                {searchQuery.length >= 2 && filteredMembers.length > 0 && `${filteredMembers.length} member${filteredMembers.length === 1 ? '' : 's'} found`}
                            </div>
                        </div>
                    ) : (
                        /* Confirmation Interface */
                        <div style={{ textAlign: 'center' }}>
                            <div style={{
                                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                                border: '2px solid #3b82f6',
                                borderRadius: '20px',
                                padding: '40px 30px',
                                marginBottom: '30px',
                                backdropFilter: 'blur(10px)'
                            }}>
                                <div style={{
                                    fontSize: '2rem',
                                    marginBottom: '10px',
                                    color: colors.text,
                                    fontWeight: '600'
                                }}>
                                    ✓ Selected Member
                                </div>
                                
                                <div style={{
                                    fontSize: '2.5rem',
                                    fontWeight: '700',
                                    color: colors.text,
                                    marginBottom: '15px'
                                }}>
                                    {selectedMember.contactName || `${selectedMember.firstName || ''} ${selectedMember.lastName || ''}`.trim()}
                                </div>

                                {/* Member Photo if available */}
                                {selectedMember.profilePicture && (
                                    <img 
                                        src={selectedMember.profilePicture}
                                        alt="Member"
                                        style={{
                                            width: '80px',
                                            height: '80px',
                                            borderRadius: '50%',
                                            objectFit: 'cover',
                                            margin: '0 auto 15px',
                                            display: 'block'
                                        }}
                                    />
                                )}
                            </div>

                            {/* Confirm Check-in Button */}
                            <button
                                type="button"
                                onClick={handleSubmit}
                                disabled={loading}
                                style={{
                                    width: '100%',
                                    padding: '25px',
                                    fontSize: '1.8rem',
                                    fontWeight: '700',
                                    backgroundColor: loading ? '#888888' : colors.success,
                                    color: '#ffffff',
                                    border: 'none',
                                    borderRadius: '15px',
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.3s ease',
                                    marginBottom: '15px',
                                    boxShadow: '0 8px 20px rgba(16, 185, 129, 0.3)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '2px'
                                }}
                                onMouseEnter={(e) => {
                                    if (!loading) {
                                        e.target.style.backgroundColor = '#059669';
                                        e.target.style.transform = 'translateY(-2px)';
                                        e.target.style.boxShadow = '0 12px 30px rgba(16, 185, 129, 0.4)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!loading) {
                                        e.target.style.backgroundColor = colors.success;
                                        e.target.style.transform = 'translateY(0px)';
                                        e.target.style.boxShadow = '0 8px 20px rgba(16, 185, 129, 0.3)';
                                    }
                                }}
                            >
                                {loading ? (
                                    <>
                                        <div style={{ 
                                            display: 'inline-block',
                                            width: '20px',
                                            height: '20px',
                                            border: '2px solid #ffffff',
                                            borderTopColor: 'transparent',
                                            borderRadius: '50%',
                                            animation: 'spin 1s linear infinite',
                                            marginRight: '10px'
                                        }}></div>
                                        Checking In...
                                    </>
                                ) : (
                                    'Confirm Check-in'
                                )}
                            </button>

                            {/* Choose Different Member Button */}
                            <button
                                type="button"
                                onClick={handleStartOver}
                                disabled={loading}
                                style={{
                                    width: '100%',
                                    padding: '15px',
                                    fontSize: '1.2rem',
                                    fontWeight: '500',
                                    backgroundColor: 'transparent',
                                    color: colors.textLight,
                                    border: `2px solid ${colors.textLight}`,
                                    borderRadius: '10px',
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.3s ease',
                                    opacity: loading ? 0.5 : 1
                                }}
                                onMouseEnter={(e) => {
                                    if (!loading) {
                                        e.target.style.backgroundColor = 'rgba(226, 232, 240, 0.1)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!loading) {
                                        e.target.style.backgroundColor = 'transparent';
                                    }
                                }}
                            >
                                Choose Different Member
                            </button>
                        </div>
                    )}
                </div>
            ) : (
                <div style={{
                    textAlign: 'center',
                    padding: '40px',
                    color: colors.textLight,
                    opacity: '0.8'
                }}>
                    Check-in is not available for this location.
                </div>
            )}
        </div>
    );
};

export default TFUNCheckIn;